import type {TFunction} from 'i18next';
import {type ProviderContext, useSnackbar} from 'notistack';
import React, {useCallback} from 'react';
import {useTranslation} from 'react-i18next';

import {ApiFailureSnackbarAction} from '../components/ApiFailureSnackbarAction';
import {getApiFailure} from '../utils/apiFailure';
import {apiFailureMessage} from '../utils/apiFailureMessage';

/**
 * Affiche en snackbar ce qu'une mutation a rejeté : la traduction de son code (cf.
 * `apiFailureMessage`), avec l'action qui va avec (passer en Premium, reconnecter LinkedIn).
 *
 * Pour les appelants qui n'ont pas d'emplacement où poser un `ApiFailureAlert` : un
 * `mutateAsync` lancé depuis un menu ou une confirmation, et dont le rejet remontait
 * jusqu'à `window.onunhandledrejection` faute d'être intercepté. Un appelant qui s'en sert
 * passe `HANDLES_API_FAILURE` à son hook de mutation, sinon le filet global affiche le même
 * refus une seconde fois.
 *
 * `message` remplace le texte du refus ; `fallback` sert quand le rejet n'est pas un
 * `ApiFailure` (exception locale).
 */
export function useApiFailureSnackbar() {
  const {enqueueSnackbar} = useSnackbar();
  const {t} = useTranslation();

  return useCallback(
    (error: unknown, options: {fallback: string; message?: string}) =>
      showApiFailureSnackbar(enqueueSnackbar, t, error, options),
    [enqueueSnackbar, t],
  );
}

/**
 * Le snackbar d'un refus, hors React : partagé par `useApiFailureSnackbar` et par le filet
 * global des mutations (`createQueryClient`), qui n'a ni hook ni contexte sous la main.
 * `preventDuplicate` : deux mutations refusées pour la même raison ne montrent qu'un message.
 */
export function showApiFailureSnackbar(
  enqueueSnackbar: ProviderContext['enqueueSnackbar'],
  t: TFunction,
  error: unknown,
  {fallback, message}: {fallback: string; message?: string},
): void {
  const failure = getApiFailure(error);
  enqueueSnackbar(message ?? (failure !== undefined ? apiFailureMessage(failure, t) : fallback), {
    variant: 'error',
    preventDuplicate: true,
    ...(failure !== undefined ? {action: () => <ApiFailureSnackbarAction code={failure.code} />} : {}),
  });
}
